<template>
    <v-dialog
        v-model="showDialog"
        scrollable
        :max-width="maxWidth"
        :fullscreen="isFullscreen"
        :hide-overlay="isFullscreen"
        :persistent="persistent"
        :transition="isFullscreen ? 'scroll-y-reverse-transition' : 'dialog-transition'"
    >
        <v-card v-if="render" class="dialog-wrapper">

            <!-- Navigation Drawer -->
            <v-navigation-drawer
                v-if="$slots.drawer"
                v-model="showDrawer"
                right
                touchless
                absolute
                hide-overlay
                stateless
                class="drawer"
            >
                <slot name="drawer" />
            </v-navigation-drawer>

            <v-card-title
                v-if="$slots.header"
                class="dialog-header"
            >
                <v-toolbar
                    dark
                    flat
                    :color="color"
                >
                    <v-btn
                        v-if="!persistent"
                        icon
                        dark
                        @click="showDialog = false"
                    >
                        <v-icon>{{ backIcon }}</v-icon>
                    </v-btn>

                    <v-toolbar-title class="header-content">
                        <slot name="header" />
                    </v-toolbar-title>
                </v-toolbar>
            </v-card-title>

            <!-- Top Progress Bar -->
            <v-progress-linear
                v-if="loading"
                class="loading"
                color="accent"
                indeterminate
                absolute
            />

            <v-divider />

            <!-- Content -->
            <v-card-text
                v-if="$slots.content"
                ref="content"
                class="dialog-content"
                :class="contentClass"
                :style="contentStyle"
            >
                <slot name="content" />
            </v-card-text>

            <v-divider />

            <!-- Footer -->
            <v-card-actions v-if="$slots.footer" class="dialog-footer">
                <slot name="footer" />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import HashGen from '@/utilities/HashGen'

export default {
    props: {
        id: {
            type: String,
            default: HashGen.regular(),
        },
        show: {
            type: Boolean,
            default: false
        },
        drawer: {
            type: String,
            default: null
        },
        color: {
            type: String,
            default: 'primary'
        },
        loading: {
            type: Boolean,
            default: false
        },
        fullscreenEnabled: {
            type: Boolean,
            default: true
        },
        forceFullscreen: {
            type: Boolean,
            default: false
        },
        contentStyle: {
            type: Object,
            default: () => {}
        },
        backIcon: {
            type: String,
            default: 'fal fa-times'
        },
        maxWidth: {
            type: String,
            default: '600px'
        },
        persistent: {
            type: Boolean,
            default: false
        },
        contentClass: {
            type: String,
            default: undefined
        }
    },

    data: () => ({
        showDialog: false,
        showDrawer: false,
        render: false,
    }),

    watch: {
        show(val) {
            this.showDialog = val;
        },

        showDialog(val) {
            this.$emit('update:show', val);

            if(val) {
                this.render = true
            } else {
                setTimeout(() => (this.render = false), 200)
            }
        },

        drawer(val) {
            this.showDrawer = !!val;
        }
    },

    computed: {
        isFullscreen() {
            if(!this.fullscreenEnabled) {
                return false
            }

            return this.forceFullscreen || this.$vuetify.breakpoint.xs || this.device?.platform === 'android';
        }
    },
};
</script>

<style lang="scss" scoped>
.dialog-wrapper {
    overflow-x: hidden;
    border-radius: 0 !important;
    border: none !important;

    .dialog-header {
        padding: 0 !important;

        ::v-deep .v-toolbar__content {
            padding: 0px 16px;

            .header-content {
                flex-grow: 1;
                display: flex;
                align-items: center;
                height: 100%;
                font-weight: 400;
            }
        }
    }

    .dialog-content {
        padding: 0 !important;
    }

    .dialog-footer {
        height: 68px;
        padding: 16px 20px;
        background: white;
    }

    .loading {
        top: 52px;

        @media screen and (min-width: 962px) {
            top: 60px;
        }
    }
}

.drawer {
    z-index: 100;
    width: 100% !important;

    &.v-navigation-drawer--close {
        transform: translateX(100%) !important;
    }
}
</style>
